import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
import { SourceKind, SourceStatus } from "./sources.js";
/**
 * `/admin/**` (CDC §7.1, lot B7.1) — administration de plateforme, gardée
 * par un compte nominatif (`AdminSessionRequest`, `auth.ts`). Sous-arbre
 * commencé par les données déjà exposées en lecture seule côté B5
 * (carrefours, itinéraires de régulation, points de retournement) : c'est ce
 * qui les fait cesser d'être structurellement vides.
 */
// --- Carrefours (admin, CDC §5.1) -----------------------------------------
export const AdminJunctionWrite = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    position: z.object({ lat: z.number(), lon: z.number() }),
    techSystem: z.string().nullable().default(null),
    operator: z.string().nullable().default(null),
    remoteControllable: z.boolean().default(false),
    impactedLineIds: z.array(OpaqueId).default([]),
});
export const AdminJunctionPatch = AdminJunctionWrite.partial();
// --- Itinéraires de régulation (admin, CDC §5.2) ---------------------------
export const AdminRegulationRouteWrite = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]).nullable().default(null),
    name: z.string().min(1),
    junctionCodes: z.array(z.string()).default([]),
    active: z.boolean().default(true),
});
export const AdminRegulationRoutePatch = AdminRegulationRouteWrite.partial();
// --- Points de retournement (admin, CDC §5.7) ------------------------------
export const AdminTurnbackStop = z.object({
    lineId: OpaqueId,
    stopId: OpaqueId,
    eligible: z.boolean(),
    // D-016 — demi-tour **sans battement** possible ici (rond-point, tiroir court) :
    // le véhicule repart en sens inverse sans temps d'arrêt au terminus.
    noLayover: z.boolean(),
});
export const AdminTurnbackStopsResponse = z.object({ stops: z.array(AdminTurnbackStop) });
export const AdminTurnbackStopWrite = z.object({
    eligible: z.boolean(),
    noLayover: z.boolean().default(false),
});
// --- Bascules fonctionnelles (admin, CDC §7.1) -----------------------------
export const AdminFeatureFlagRow = z.object({
    key: z.string(),
    /** Valeur du fichier de config réseau, avant toute surcharge admin. */
    configDefault: z.boolean(),
    /** `null` = pas de surcharge posée, le défaut du fichier s'applique. */
    override: z.boolean().nullable(),
    /** Ce que `GET /config` sert réellement à l'instant présent. */
    effective: z.boolean(),
    updatedAt: IsoDateTime.nullable(),
    updatedByUsername: z.string().nullable(),
});
export const AdminFeatureFlagsResponse = z.object({ flags: z.array(AdminFeatureFlagRow) });
export const AdminFeatureFlagWrite = z.object({ enabled: z.boolean() });
// --- Sources temps réel (admin, CDC §2.3, §7.1) ----------------------------
export const AdminSourceRow = z.object({
    sourceId: z.string(),
    operatorId: z.string(),
    kind: SourceKind,
    /** Dernier état de santé réellement observé — reste au dernier connu si `effectiveEnabled` est `false`. */
    status: SourceStatus.nullable(),
    lastDataAt: IsoDateTime.nullable(),
    lastEntityCount: z.number().int().nonnegative(),
    fallback: z.enum(["none", "scheduled"]).nullable(),
    /** Valeur du fichier de config réseau. */
    configEnabled: z.boolean(),
    /** Surcharge admin — `null` = pas de surcharge posée. */
    enabledOverride: z.boolean().nullable(),
    /** Ce que le worker d'ingestion applique réellement (surcharge sinon config). */
    effectiveEnabled: z.boolean(),
    /** Dernière demande de resynchronisation posée (signal à écriture seule) — `null` si aucune. */
    resyncRequestedAt: IsoDateTime.nullable(),
});
export const AdminSourcesResponse = z.object({ sources: z.array(AdminSourceRow) });
export const AdminSourceEnabledWrite = z.object({ enabled: z.boolean() });
// --- Réseaux (admin, CDC §7.1 / §7.2 — ajout/retrait à chaud, B7.5) --------
export const AdminNetworkRow = z.object({
    id: z.string(),
    name: z.string(),
    /** État demandé (colonne `networks.enabled`). */
    enabled: z.boolean(),
    /** `file` : défini par un fichier de config. `db` : géré via cet endpoint. */
    source: z.enum(["file", "db"]),
    /** `true` si une config complète est stockée en base pour ce réseau. */
    configStored: z.boolean(),
    configUpdatedAt: IsoDateTime.nullable(),
    /** Condensé de la config **effective** (celle que sert l'API), `null` si le réseau est désactivé. */
    effectiveDigest: z.string().nullable(),
    /** `true` si le réseau est réellement servi par l'API en ce moment. */
    enabledEffective: z.boolean(),
});
export const AdminNetworksResponse = z.object({ networks: z.array(AdminNetworkRow) });
/** Réponse de `PUT` / `PATCH /admin/networks/{n}` : la ligne à jour + le diff de rechargement. */
export const AdminNetworkMutationResponse = z.object({
    network: AdminNetworkRow,
    reload: z.object({ added: z.array(z.string()), removed: z.array(z.string()) }),
});
/** Corps de `PATCH /admin/networks/{n}`. `PUT` prend une config réseau complète (voir §2 du contrat). */
export const AdminNetworkEnabledWrite = z.object({ enabled: z.boolean() });
// --- Documents d'exploitation PDF (admin, CDC §5.8 / §7.1, B6.1) -----------
export const AdminPdfDocumentKind = z.enum(["running-board", "driver-duty"]);
export const AdminPdfDatasetRow = z.object({
    id: z.string(),
    documentKind: AdminPdfDocumentKind,
    /** Identité complète du document, période comprise (« metro-rouge », « navettes-vert », « services-bus »). */
    sourceLabel: z.string(),
    /** Libellé de régime (« ROUGE », « VERTE »…) pour un tableau de marche ; `null` pour un jeu services conducteurs. */
    period: z.string().nullable(),
    version: z.number().int(),
    status: z.enum(["imported", "active", "superseded", "rolledback"]),
    checksum: z.string(),
    validFrom: z.string().nullable(), // YYYY-MM-DD
    validUntil: z.string().nullable(),
    stats: z.record(z.number()),
    importedAt: IsoDateTime,
});
export const AdminPdfDatasetsResponse = z.object({ datasets: z.array(AdminPdfDatasetRow) });
/**
 * Réponse de `POST /admin/networks/{n}/pdf-datasets` (multipart : `file` +
 * `documentKind` + `sourceLabel` + `activate?`). `201` pour un nouvel import,
 * `200` si le même document (checksum) était déjà actif (`reused: true`).
 */
export const AdminPdfImportResult = z.object({
    datasetId: z.string(),
    version: z.number().int(),
    reused: z.boolean(),
    activated: z.boolean(),
    period: z.string().nullable(),
    validFrom: z.string().nullable(),
    pageCount: z.number().int().nonnegative(),
    /** Régimes distincts rencontrés dans les en-têtes — >1 = document hétérogène, à signaler. */
    distinctPeriods: z.array(z.string()),
});
// --- Journal d'audit d'administration (CDC §7.1) ---------------------------
export const AuditLogEntry = z.object({
    id: z.string(),
    username: z.string(), // compte admin nominatif — exception assumée (CDC §7.3)
    action: z.enum(["create", "update", "delete"]),
    resource: z.string(),
    resourceId: z.string().nullable(),
    networkId: z.string().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    at: IsoDateTime,
});
export const AuditLogResponse = z.object({ entries: z.array(AuditLogEntry) });
// --- Tracé de ligne : override admin (D-019 phase C, CDC §7.1) -------------
/**
 * Provenance d'un tracé HD stocké. `pfaedle` : map-matching automatique
 * (rejoué à chaque `shapes-prepare`). `osm-relation` / `manual` : **overrides
 * curés en admin** — `pfaedle` ne les écrase pas.
 */
export const LineShapeSource = z.enum(["pfaedle", "osm-relation", "manual"]);
export const AdminLineShape = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    source: LineShapeSource,
    pointCount: z.number().int().nonnegative(),
    stopSeqHash: z.string(),
    computedAt: IsoDateTime,
});
export const AdminLineShapeResponse = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    hd: AdminLineShape.nullable(), // tracé HD stocké, ou null (l'API sert alors le shapes.txt brut)
    gtfsShapeAvailable: z.boolean(), // un tracé brut existe-t-il en repli ?
});
// --- Gestion des accès à la porte d'application (admin, v1.40, CDC §7.1) ---
//
// CRUD des accès applicatifs (`app_users` — porte de login principale, §8bis)
// depuis le panneau d'administration, en plus du script hors-ligne
// `apps/api/src/bin/user.ts`. Portée : les **accès applicatifs** seulement ;
// les **comptes admin** (`admin_accounts`) restent provisionnés hors-ligne
// (`bin/create-admin.ts`) — la racine de confiance ne s'auto-administre pas
// par le réseau.
export const AdminAppUserRow = z.object({
    username: z.string(),
    createdAt: IsoDateTime,
    revokedAt: IsoDateTime.nullable(),
});
export const AdminAppUsersResponse = z.object({ users: z.array(AdminAppUserRow) });
/**
 * `POST /admin/app-users`. `username` = **nom de compte** non nominatif
 * (lettres, chiffres, `.`, `_`, `-` ; commence par un alphanumérique).
 * `password` ≥ 12 caractères (jamais renvoyé). `409` si le nom existe déjà.
 */
export const AdminAppUserCreate = z.object({
    username: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, "lettres, chiffres, . _ - (commence par un alphanumérique)"),
    password: z.string().min(12),
});
/** Corps de `PUT /admin/networks/{n}/lines/{id}/shape/{direction}`. */
export const AdminLineShapeWrite = z.discriminatedUnion("kind", [
    // dépose une géométrie GeoJSON à la main (fallback ultime)
    z.object({
        kind: z.literal("manual"),
        geojson: z.object({
            type: z.literal("LineString"),
            coordinates: z.array(z.tuple([z.number(), z.number()])).min(2),
        }),
    }),
    // fige la géométrie d'une relation OSM `type=route` (snapshot Overpass validé)
    z.object({
        kind: z.literal("osm-relation"),
        relationId: z.number().int().positive(),
    }),
]);
//# sourceMappingURL=admin.js.map